import {
	Cta,
	PersonBenefits,
	SalesforceVouchers,
	VoucherStatus,
	VouchersCampaign,
	VWDataVoucherDestination,
	VWDataVouchers,
} from './models/benefits';

export type VoucherSource = 'vwData' | 'salesforce';

export interface NormalizedVoucherCard {
	title: string; // Ambos
	imageUrl: string; // Ambos
	imageVerticalUrl?: string; // Salesforce
	description?: string; // Salesforce
	modoEmpleo?: string; // Salesforce
	cta?: Cta; // Ambos
}

export interface NormalizedVoucherLayer {
	title: string;
	imageUrl: string;
	description: string;
	modoEmpleo: string;
	legal: string;
	cta: Cta;
}

export interface NormalizedVoucher {
	id: string; // Ambos
	name?: string; // Salesforce
	priority: number; // Ambos
	type?: string; // Ambos
	campaign?: VouchersCampaign; // Salesforce
	amount?: number; // Salesforce
	validityDate: number | Date | string; // Ambos
	expirationDate: number | Date | string | null; // Ambos
	enjoyedDate: number | Date | string | null; // Ambos
	destination?: VWDataVoucherDestination; // Data
	isLayer?: boolean; // Data
	card: NormalizedVoucherCard; // Ambos
	legalText: string; // Ambos
	layer: NormalizedVoucherLayer | null; // Data
	status: VoucherStatus; // Ambos
	voucherStatus?: string; // Salesforce
	vin?: string; // Ambos
	registrationPlate?: string; // Salesforce
	fullName?: string; // Salesforce
	creationDate?: number | Date | string; // Salesforce
	email?: string; // Salesforce
	value?: string; // Salesforce
	activationExpirationDate?: number | Date | string | null; // Salesforce
	redemptionExpirationDate?: number | Date | string | null; // Salesforce
	isPublished?: boolean; // Salesforce
	source: VoucherSource; // Ambos
	isDestacado: boolean; // Solo data pero forzado en la normalización de Salesforce
}

type VoucherCollections = Pick<PersonBenefits['benefits'], 'vwDataVouchers' | 'salesforceVouchers'>;

const isPersonBenefits = (
	value: PersonBenefits | VoucherCollections | VWDataVouchers | SalesforceVouchers,
): value is PersonBenefits => 'benefits' in value;

const isVoucherCollections = (
	value: PersonBenefits | VoucherCollections | VWDataVouchers | SalesforceVouchers,
): value is VoucherCollections => 'vwDataVouchers' in value || 'salesforceVouchers' in value;

const isSalesforceVoucher = (
	voucher: PersonBenefits | VoucherCollections | VWDataVouchers | SalesforceVouchers,
): voucher is SalesforceVouchers => 'vgedMKTVoucherTitle' in voucher || 'vgedMKTVoucherImage1' in voucher;

const isVWDataVoucher = (
	voucher: PersonBenefits | VoucherCollections | VWDataVouchers | SalesforceVouchers,
): voucher is VWDataVouchers => 'card' in voucher && 'layer' in voucher;

const hasLayerContent = (layer?: VWDataVouchers['layer'] | null): boolean => {
	if (!layer) return false;

	return Boolean(layer.title || layer.imageUrl || layer.description || layer.modoEmpleo);
};

const isVWDataCarDestination = (destination: VWDataVoucherDestination): boolean => destination === 'ForCars';

export const normalizeVWDataVoucher = (voucher: VWDataVouchers): NormalizedVoucher => {
	const isLayer = voucher.isLayer;
	const layer: NormalizedVoucherLayer | null =
		isLayer && hasLayerContent(voucher.layer)
			? {
					title: voucher.layer.title ?? '',
					imageUrl: voucher.layer.imageUrl ?? '',
					description: voucher.layer.description ?? '',
					modoEmpleo: voucher.layer.modoEmpleo ?? '',
					legal: voucher.layer.legal ?? '',
					cta: voucher.layer.cta,
				}
			: null;

	return {
		id: voucher.id,
		priority: voucher.priority,
		type: voucher.type,
		validityDate: voucher.validityDate ?? '',
		expirationDate: voucher.expirationDate ?? null,
		enjoyedDate: null,
		destination: voucher.destination,
		isLayer,
		vin: isVWDataCarDestination(voucher.destination) ? voucher.vin : undefined,
		legalText: voucher.layer?.legal ?? '',
		card: voucher.card,
		layer,
		status: voucher.status,
		source: 'vwData',
		isDestacado: voucher.isDestacado,
	};
};

export const normalizeSalesforceVoucher = (voucher: SalesforceVouchers): NormalizedVoucher => {
	return {
		id: voucher.id,
		name: voucher.name,
		priority: voucher.priority,
		type: voucher.vgedVoucherType,
		campaign: voucher.vgedCampaign,
		amount: voucher.vgedAmount,
		validityDate: voucher.vgedActivationDate,
		expirationDate: voucher.vgedExpirationDate ?? null,
		enjoyedDate: voucher.vgedRedeemedDate ?? null,
		legalText: voucher.vgedMKTVoucherDisclaimer,
		card: {
			title: voucher.vgedMKTVoucherTitle,
			imageUrl: voucher.vgedMKTVoucherImage1,
			imageVerticalUrl: voucher.vgedMKTVoucherImage2,
			description: voucher.vgedVoucherDescription,
			modoEmpleo: voucher.vgedMKTVoucherText,
			cta: {
				src: voucher.vgedMKTURLLink,
				label: voucher.vgedMKTURLLabel,
			},
		},
		layer: null,
		status: voucher.status,
		voucherStatus: voucher.pklVoucherStatusC,
		vin: voucher.vgedVin,
		registrationPlate: voucher.vgedRegistrationPlate,
		fullName: voucher.vgedFullName,
		creationDate: voucher.vgedCreationDate,
		email: voucher.vgedEmail,
		value: voucher.vgedValue,
		activationExpirationDate: voucher.vgedActivationExpirationDate,
		redemptionExpirationDate: voucher.vgedRedemptionExpirationDate,
		isPublished: voucher.vgedMKTIsPublished,
		source: 'salesforce',
		isDestacado: true,
	};
};

export function normalizeVouchers(benefitsOrCollections: PersonBenefits | VoucherCollections): NormalizedVoucher[];
export function normalizeVouchers(
	voucher: VWDataVouchers | SalesforceVouchers | null | undefined,
): NormalizedVoucher | null;
export function normalizeVouchers(
	benefitsOrCollectionsOrVoucher:
		| PersonBenefits
		| VoucherCollections
		| VWDataVouchers
		| SalesforceVouchers
		| null
		| undefined,
): NormalizedVoucher[] | NormalizedVoucher | null {
	if (!benefitsOrCollectionsOrVoucher) return null;

	if (isSalesforceVoucher(benefitsOrCollectionsOrVoucher)) {
		return normalizeSalesforceVoucher(benefitsOrCollectionsOrVoucher);
	}

	if (isVWDataVoucher(benefitsOrCollectionsOrVoucher)) {
		return normalizeVWDataVoucher(benefitsOrCollectionsOrVoucher);
	}

	const collections = isPersonBenefits(benefitsOrCollectionsOrVoucher)
		? benefitsOrCollectionsOrVoucher.benefits
		: isVoucherCollections(benefitsOrCollectionsOrVoucher)
			? benefitsOrCollectionsOrVoucher
			: { vwDataVouchers: [], salesforceVouchers: [] };

	return [
		...(collections.vwDataVouchers ?? []).map(normalizeVWDataVoucher),
		...(collections.salesforceVouchers ?? []).map(normalizeSalesforceVoucher),
	].sort((firstVoucher, secondVoucher) => secondVoucher.priority - firstVoucher.priority);
}
